import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { SearchResultItem } from '../types';
import { SHOW_REASON } from '../api/client';
import { readDetailState, type DetailState } from '../nav';
import { refreshTrayThumb } from '../tray';
import { ko } from '../vocab';
import { Icon } from './Icon';
import SaveToggle from './SaveToggle';
import Swatch from './Swatch';

type MetaPart = { text: string; warn?: boolean; title?: string; unverified?: boolean };

const UNVERIFIED_TITLE = '색 보정 없는 촬영이라 주 색상을 확인하지 못했습니다';

/**
 * 색 · 패턴 · 소재 · 재고 메타. 'unknown' · 사전에 없는 코드값은 그리지 않는다 (리뷰 4회차 m3 · 6회차 m30).
 * 색은 서버가 색 패싯 · 색상어 검색과 같은 근거만 준다 — 색 보정 없는 촬영의 측정 기반 색은 「색 미확인」 (2026-10-18).
 */
function metaOf(item: SearchResultItem): MetaPart[] {
  const s = item.summary ?? {};
  const out: MetaPart[] = [];
  const color = s.dominant_color ? ko(s.dominant_color, 'color', null) : null;
  const pattern = s.pattern ? ko(s.pattern, 'pattern', null) : null;
  if (color) out.push({ text: color });
  else if (s.color_unverified) {
    // 정정 보조 색상으로는 색 필터에 잡힐 수 있어 「검색에 안 잡힌다」고 말하지 않는다 — 주 색상 이야기만 (Codex 검토)
    out.push({ text: '색 미확인', title: UNVERIFIED_TITLE, unverified: true });
  }
  if (pattern) out.push({ text: pattern });
  if (s.material_composition && s.material_composition !== 'unknown') out.push({ text: s.material_composition });
  if (s.inventory_yardage === 0) out.push({ text: '재고 없음', warn: true });
  else if (s.inventory_yardage != null) out.push({ text: `재고 ${s.inventory_yardage} yd` });
  return out;
}

function Meta({ parts, className }: { parts: MetaPart[]; className: string }) {
  if (!parts.length) return null;
  return (
    <div className={className}>
      {parts.map((p, i) => (
        <span key={i} className={p.warn ? 'warn' : undefined} title={p.title}>{i > 0 && ' · '}{p.text}</span>
      ))}
    </div>
  );
}

/**
 * 원단 카드 (R1-8 · 컨셉 §7.4 · §7.7 「사진이 이끄는 화면」 · 루브릭 R5-1).
 *
 * grid — 견본 사진(촬영대 틀을 걷어 낸 원단 부분, swatch_url → thumb_url → 빈 견본)이 카드 위쪽을 가득 채우고(카드 면적의 70% 이상),
 *        아래는 이름(14 · 굵게) · 품번(12 · 고정폭) 두 줄. 점수 · 순위는 품번 줄 오른쪽(사진 위에 글자를 얹지 않는다).
 *        색 · 패턴 · 소재 · 재고와 추천 사유는 PC 에서 마우스를 올리거나 키보드로 카드에 오면 사진 아래쪽에 단색 표면 칸으로 보이고,
 *        같은 글이 링크 이름에 들어 있어 화면 낭독기는 늘 읽는다. 휴대폰에서는 상세(색 팔레트 · 요약 줄)와 「목록 보기」(사유 · 메타 전부)에서.
 *        (리뷰 6회차 M19 의 「호버로만」 은 목록 보기 · 상세가 같은 정보를 늘 보여 줘 해소 — 컨셉 §7.7 이 정한 배치)
 *        「색 미확인」 은 데이터 신뢰 표시라 사진 밖 이름 줄 끝에도 작은 아이콘(접근 이름 「색 미확인」)을 둔다 — 휴대폰 격자에서도 보인다(15회차 권고).
 * list — 검색 엔진 결과처럼 한 줄에 하나: 72px 견본 · 이름(링크) · 품번 · 추천 사유 · 메타(모두 늘 보인다).
 *
 * rank 를 주면 점수 대신 순위(#1, #2…)를 보인다. 유사 원단의 RRF 점수(0.02~0.07)를
 * 검색 결과의 정규화 점수(1.00)와 같은 배지로 보이면 "4% 유사" 로 오독된다 (리뷰 2회차 M12).
 * ids — 이 카드가 속한 결과의 순서(검색 · 사진 검색 · 비슷한 원단), list — 전체 목록의 정렬 · 필터.
 * 상세의 PREV / NEXT 가 이것을 따른다 (컨셉 §7.5).
 *
 * 「담기」(계획서 U1): 격자는 썸네일 왼쪽 위 아이콘 버튼, 목록은 제목 줄 오른쪽 아이콘 + 글자.
 * 링크 안에 버튼을 넣지 않는다(대화형 요소 중첩 금지) — 카드 틀(article) 안에 링크와 버튼을 나란히 둔다.
 */
export default function FabricCard({ item, rank, ids, list, variant = 'grid', showScore = true, defer = false }: {
  item: SearchResultItem; rank?: number;
  ids?: string[];
  list?: DetailState['list'];
  variant?: 'grid' | 'list';
  /** 홈 「최근 등록」처럼 점수가 의미 없는 목록은 끈다 */
  showScore?: boolean;
  /** 화면 가까이 올 때까지 사진을 받지 않는다(홈 첫 화면 밖 격자 — near.ts, 15회차 M33) */
  defer?: boolean;
}) {
  const loc = useLocation();
  const to = `/fabric/${item.fabric_id}`;
  // from — 상세의 「검색 결과로 / 목록으로」 가 돌아갈 곳(쿼리 · 페이지 포함). 상세 안의 「비슷한 원단」
  // 카드는 원래 목록을 이어받는다.
  const here = readDetailState(loc.state);
  const from = loc.pathname.startsWith('/fabric/') && here.from ? here.from : loc.pathname + loc.search;
  const meta = metaOf(item);
  const reasonText = SHOW_REASON !== 'never' && item.reason ? item.reason : null;
  const base: DetailState = list ? { list, from } : ids?.length ? { ids, from } : { from };
  const state: DetailState = reasonText ? { ...base, why: { id: item.fabric_id, text: reasonText } } : base;
  const tray = { fabric_id: item.fabric_id, fabric_name: item.fabric_name, part_number: item.part_number, thumb_url: item.thumb_url,
                 swatch_url: item.swatch_url ?? null, swatch_sm_url: item.swatch_sm_url ?? null, color: item.summary?.dominant_color ?? null };
  // 트레이에 있는 원단이면 새 서명 주소로 견본 · 썸네일을(15분 뒤 트레이 그림이 깨지지 않게), 지금 근거의 주 색상으로 색을 갱신해 둔다
  useEffect(() => { refreshTrayThumb(item.fabric_id, item.thumb_url, tray.color, item.swatch_url, item.swatch_sm_url); },
    [item.fabric_id, item.thumb_url, item.swatch_url, item.swatch_sm_url, tray.color]);
  const reason = reasonText;
  const score = !showScore ? null
    : rank ? { text: `#${rank}`, title: '유사도 순위' }
      : { text: item.score.toFixed(2), title: '검색 점수 (1.00 = 최상위)' };

  if (variant === 'list') {
    return (
      <article className="res-row">
        {/* 썸네일 링크는 이름 링크와 같은 곳 — 탭 순서에서 한 번만 멈추게 숨긴다 */}
        <Link to={to} state={state} className="res-row-thumb" tabIndex={-1} aria-hidden="true">
          <Swatch fabricId={item.fabric_id} url={item.swatch_url} small={item.swatch_sm_url} sizes="72px" fallback={item.thumb_url} className="swatch" alt="" iconSize={20}
                  defer={defer} priority={defer ? 'low' : undefined} />
        </Link>
        <div className="res-row-body">
          <div className="res-row-title">
            <Link to={to} state={state} className="res-row-name">{item.fabric_name}</Link>
            {score && <span className="res-row-score">{rank ? `유사 순위 ${score.text}` : `점수 ${score.text}`}</span>}
            <SaveToggle item={tray} className="res-row-save" />
          </div>
          {item.part_number && <div className="res-row-pn">{item.part_number}</div>}
          {reason && <p className="res-row-reason">{reason}</p>}
          <Meta parts={meta} className="res-row-meta" />
        </div>
      </article>
    );
  }

  const peek = meta.length > 0 || !!reason;
  const unverified = meta.some((x) => x.unverified);
  // 낭독 이름 — 「색 미확인」 은 이름 줄의 아이콘(접근 이름)이 이미 말하므로 sr-only 에서는 뺀다(두 번 읽지 않게)
  const srMeta = meta.filter((x) => !x.unverified).map((x) => x.text);
  return (
    <article className={peek ? 'card has-peek' : 'card'}>
      <Link to={to} className="card-link" state={state}>
        <div className="card-thumb">
          <Swatch fabricId={item.fabric_id} url={item.swatch_url} small={item.swatch_sm_url} fallback={item.thumb_url} className="swatch" alt=""
                  sizes="(max-width: 720px) calc(50vw - 22px), (max-width: 1023px) calc(25vw - 15px), 180px" defer={defer} priority={defer ? 'low' : undefined} />
          {/* 마우스 올림 · 키보드 포커스 때 사진 아래쪽에 단색 표면 칸(보일 때만 그린다 — 숨은 동안은 visibility: hidden).
              화면 낭독기에는 아래 sr-only 한 줄이 늘 같은 내용을 링크 이름으로 읽힌다 */}
          {peek && (
            <div className="card-peek" aria-hidden="true">
              <Meta parts={meta} className="card-meta" />
              {reason && <p className="card-reason">{reason}</p>}
            </div>
          )}
        </div>
        <div className="card-body">
          {unverified ? (
            <div className="card-name has-mark">
              <span className="card-name-t">{item.fabric_name}</span>
              <span className="card-mark"><Icon name="palette" size={14} title="색 미확인" /></span>
            </div>
          ) : <div className="card-name">{item.fabric_name}</div>}
          {/* 리뷰 m15: 품번이 없으면 식별번호를 품번처럼 보이지 않는다(빈 줄로 높이만 지킨다) */}
          <div className="card-pn">
            <span className="card-pn-t">{item.part_number ?? ''}</span>
            {score && <span className="card-score" title={score.title}>{score.text}</span>}
          </div>
          {(srMeta.length > 0 || !!reason) && <span className="sr-only">{` — ${srMeta.join(' · ')}${reason ? `${srMeta.length ? '. ' : ''}${reason}` : ''}`}</span>}
        </div>
      </Link>
      <SaveToggle item={tray} variant="overlay" />
    </article>
  );
}
